// components/PositionDetailModal.js
import React, { useState, useMemo } from 'react';
import { Modal, Badge, Button, Card, Collapse } from 'react-bootstrap';
import MarketChart from './MarketChart';

/**
 * Comprehensive modal showing full position details with ALL agent story data
 * Includes: position summary, chart, and complete AI evaluation details
 * DARK THEME VERSION - Matches black theme throughout the app
 */
const PositionDetailModal = ({ 
  position, 
  chartData, 
  evaluations = [], 
  show, 
  onHide,
  onViewFullStory 
}) => {
  const [expandedEval, setExpandedEval] = useState(null);
  const [showAllEvaluations, setShowAllEvaluations] = useState(false);

  // Format helpers
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return 'N/A';
    return Number(num).toFixed(decimals);
  };

  // Filter and sort evaluations for this position
  const positionEvaluations = useMemo(() => {
    if (!evaluations || evaluations.length === 0 || !position) return [];
    
    const entryTime = new Date(position.entryTime);
    const exitTime = position.exitTime ? new Date(position.exitTime) : new Date();
    
    return evaluations
      .filter(evaluation => {
        const evalTime = new Date(evaluation.timestamp);
        const isInTimeWindow = evalTime >= entryTime && evalTime <= exitTime;
        const matchesPositionId = evaluation.position_id === position.id || 
                                  evaluation.eval_position_id === position.id;
        return matchesPositionId || isInTimeWindow;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [evaluations, position]);

  const proactiveEvals = positionEvaluations.filter(e => e.type === 'PROACTIVE');
  const reactiveEvals = positionEvaluations.filter(e => e.type === 'REACTIVE');
  const evalsToShow = showAllEvaluations ? positionEvaluations : positionEvaluations.slice(0, 5);

  const getDecisionColor = (decision) => {
    const colors = {
      'PROCEED': 'success', 'SKIP': 'secondary', 'ADJUST': 'warning',
      'HOLD': 'info', 'CLOSE': 'danger', 'EXECUTE': 'success'
    };
    return colors[decision] || 'dark';
  };

  const getDecisionIcon = (decision) => {
    const icons = {
      'PROCEED': '✓', 'SKIP': '⊗', 'ADJUST': '🔄',
      'HOLD': '⏸', 'CLOSE': '🚪', 'EXECUTE': '✓'
    };
    return icons[decision] || '•';
  };

  // Early return AFTER all hooks
  if (!position) return null;

  // Calculate position metrics
  const entryPrice = parseFloat(position.entryPrice) || 0;
  const currentPrice = parseFloat(position.currentPrice) || 0;
  const exitPrice = position.exitTime ? currentPrice : null;
  const qty = parseInt(position.qty) || 0;
  const isLong = position.isLong;
  const isClosed = !!position.exitTime;
  const unrealizedPL = parseFloat(position.unrealizedPL) || 0;
  const plPercent = entryPrice > 0 ? ((Math.abs(unrealizedPL) / (entryPrice * qty)) * 100) : 0;
  const isWin = unrealizedPL >= 0;

  // Calculate hold time
  const entryTime = new Date(position.entryTime);
  const exitTime = position.exitTime ? new Date(position.exitTime) : new Date();
  const holdTimeMs = exitTime - entryTime;
  const holdTimeMin = Math.round(holdTimeMs / 1000 / 60);
  const holdTimeHours = Math.floor(holdTimeMin / 60);
  const holdTimeMinRemainder = holdTimeMin % 60;
  const holdTimeDisplay = holdTimeHours > 0 
    ? `${holdTimeHours}h ${holdTimeMinRemainder}m` 
    : `${holdTimeMin}m`;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl"
      fullscreen="lg-down"
      centered
      scrollable
    >
      <div style={{
        backgroundColor: '#2a2a2a',
        borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        overflow: 'hidden'
      }}>
      <Modal.Header 
        closeButton 
        style={{ 
          backgroundColor: '#2a2a2a', 
          borderBottom: '1px solid #444',
          color: '#fff'
        }}
        closeVariant="white"
      >
        <Modal.Title>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff' }}>{position.symbol}</span>
            <Badge bg={isLong ? 'success' : 'danger'} style={{ fontSize: '14px' }}>
              {isLong ? '🟢 LONG' : '🔴 SHORT'}
            </Badge>
            <Badge bg={isClosed ? 'secondary' : 'primary'} style={{ fontSize: '14px' }}>
              {isClosed ? 'CLOSED' : 'ACTIVE'}
            </Badge>
            {isClosed && (
              <Badge bg={isWin ? 'success' : 'danger'} style={{ fontSize: '14px' }}>
                {isWin ? '✓ WIN' : '✗ LOSS'}
              </Badge>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ 
        maxHeight: '80vh', 
        overflowY: 'auto',
        backgroundColor: '#2a2a2a',
        color: '#e0e0e0'
      }}>
        
        {/* Position Summary Card */}
        <Card className="mb-3" style={{ backgroundColor: '#333', border: '1px solid #444' }}>
          <Card.Body>
            <div className="row g-3">
              {/* Entry Info */}
              <div className="col-md-3">
                <small className="d-block" style={{ color: '#888' }}>Entry Time</small>
                <strong style={{ color: '#e0e0e0' }}>{formatTime(position.entryTime)}</strong>
                <div><small style={{ color: '#888' }}>{formatDate(position.entryTime)}</small></div>
              </div>
              <div className="col-md-3">
                <small className="d-block" style={{ color: '#888' }}>Entry Price</small>
                <strong style={{ fontSize: '18px', color: '#e0e0e0' }}>${formatNumber(entryPrice)}</strong>
              </div>
              <div className="col-md-3">
                <small className="d-block" style={{ color: '#888' }}>Quantity</small>
                <strong style={{ fontSize: '18px', color: '#e0e0e0' }}>{qty} shares</strong>
              </div>
              <div className="col-md-3">
                <small className="d-block" style={{ color: '#888' }}>Entry Value</small>
                <strong style={{ color: '#e0e0e0' }}>${formatNumber(entryPrice * qty)}</strong>
              </div>

              {/* Exit Info */}
              {isClosed && (
                <>
                  <div className="col-md-3">
                    <small className="d-block" style={{ color: '#888' }}>Exit Time</small>
                    <strong style={{ color: '#e0e0e0' }}>{formatTime(position.exitTime)}</strong>
                    <div><small style={{ color: '#888' }}>{formatDate(position.exitTime)}</small></div>
                  </div>
                  <div className="col-md-3">
                    <small className="d-block" style={{ color: '#888' }}>Exit Price</small>
                    <strong style={{ fontSize: '18px', color: '#e0e0e0' }}>${formatNumber(exitPrice)}</strong>
                  </div>
                  <div className="col-md-3">
                    <small className="d-block" style={{ color: '#888' }}>Hold Time</small>
                    <strong style={{ fontSize: '18px', color: '#e0e0e0' }}>{holdTimeDisplay}</strong>
                  </div>
                  <div className="col-md-3">
                    <small className="d-block" style={{ color: '#888' }}>Exit Value</small>
                    <strong style={{ color: '#e0e0e0' }}>${formatNumber(exitPrice * qty)}</strong>
                  </div>
                </>
              )}

              {/* Current Info (Active position) */}
              {!isClosed && (
                <>
                  <div className="col-md-3">
                    <small className="d-block" style={{ color: '#888' }}>Current Price</small>
                    <strong style={{ fontSize: '18px', color: '#e0e0e0' }}>${formatNumber(currentPrice)}</strong>
                  </div>
                  <div className="col-md-3">
                    <small className="d-block" style={{ color: '#888' }}>Hold Time</small>
                    <strong style={{ fontSize: '18px', color: '#e0e0e0' }}>{holdTimeDisplay}</strong>
                  </div>
                  <div className="col-md-3">
                    <small className="d-block" style={{ color: '#888' }}>Current Value</small>
                    <strong style={{ color: '#e0e0e0' }}>${formatNumber(currentPrice * qty)}</strong>
                  </div>
                </>
              )}

              {/* P&L Summary */}
              <div className="col-12">
                <div 
                  className="p-3 rounded" 
                  style={{ 
                    backgroundColor: isWin ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    border: `2px solid ${isWin ? '#10b981' : '#ef4444'}`
                  }}
                >
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <small className="d-block" style={{ color: '#888' }}>
                        {isClosed ? 'Final P&L' : 'Unrealized P&L'}
                      </small>
                      <strong style={{ 
                        fontSize: '28px', 
                        color: isWin ? '#10b981' : '#ef4444' 
                      }}>
                        {isWin ? '+' : ''}${formatNumber(unrealizedPL)}
                      </strong>
                    </div>
                    <div className="col-md-4">
                      <small className="d-block" style={{ color: '#888' }}>P&L Percentage</small>
                      <strong style={{ 
                        fontSize: '28px', 
                        color: isWin ? '#10b981' : '#ef4444' 
                      }}>
                        {isWin ? '+' : ''}{formatNumber(plPercent)}%
                      </strong>
                    </div>
                    <div className="col-md-4">
                      <small className="d-block" style={{ color: '#888' }}>Price Movement</small>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '16px', color: '#e0e0e0' }}>${formatNumber(entryPrice)}</span>
                        <span style={{ fontSize: '20px', color: '#6c757d' }}>→</span>
                        <span style={{ fontSize: '16px', color: '#e0e0e0' }}>
                          ${formatNumber(isClosed ? exitPrice : currentPrice)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Target/Stop Info */}
              {(position.target > 0 || position.stop > 0) && (
                <>
                  {position.target > 0 && (
                    <div className="col-md-4">
                      <small className="d-block" style={{ color: '#888' }}>Target Price</small>
                      <strong style={{ color: '#10b981' }}>${formatNumber(position.target)}</strong>
                    </div>
                  )}
                  {position.stop > 0 && (
                    <div className="col-md-4">
                      <small className="d-block" style={{ color: '#888' }}>Stop Loss</small>
                      <strong style={{ color: '#ef4444' }}>${formatNumber(position.stop)}</strong>
                    </div>
                  )}
                  {position.rrRatio > 0 && (
                    <div className="col-md-4">
                      <small className="d-block" style={{ color: '#888' }}>Risk/Reward Ratio</small>
                      <strong style={{ color: '#e0e0e0' }}>1:{position.rrRatio}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Market Chart Section */}
        {chartData && chartData.length > 0 && (
          <Card className="mb-3" style={{ backgroundColor: '#333', border: '1px solid #444' }}>
            <Card.Header style={{ backgroundColor: '#333', borderBottom: '1px solid #444' }}>
              <h6 className="mb-0" style={{ color: '#e0e0e0' }}>📊 Position Chart</h6>
            </Card.Header>
            <Card.Body style={{ padding: '15px', backgroundColor: '#2a2a2a' }}>
              <div style={{ height: '400px', width: '100%' }}>
                <MarketChart 
                  data={chartData}
                  selectedPosition={position}
                  compact={true}
                />
              </div>
            </Card.Body>
          </Card>
        )}

        {/* AI Evaluations Section */}
        <Card className="mb-3" style={{ backgroundColor: '#333', border: '1px solid #444' }}>
          <Card.Header style={{ backgroundColor: '#333', borderBottom: '1px solid #444' }}>
            <h6 className="mb-0" style={{ color: '#e0e0e0' }}>🤖 AI Evaluations & Agent Story</h6>
            <small style={{ color: '#888' }}>
              {positionEvaluations.length} total 
              ({proactiveEvals.length} proactive, {reactiveEvals.length} reactive)
            </small>
          </Card.Header>
          <Card.Body style={{ backgroundColor: '#2a2a2a' }}>
            {positionEvaluations.length === 0 ? (
              <div className="text-center py-4" style={{ color: '#888' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
                <p>No AI evaluations found for this position</p>
                <small>This might indicate the position was manually entered or evaluation data is missing.</small>
              </div>
            ) : (
              <>
                {evalsToShow.map((evaluation, index) => {
                  const isExpanded = expandedEval === evaluation.id;
                  const agentStory = evaluation.fullData?.eval_agent_story || {};
                  const hasAgentStory = Object.keys(agentStory).length > 0;
                  
                  return (
                    <Card 
                      key={evaluation.id || index}
                      className="mb-3"
                      style={{ 
                        borderLeft: `4px solid ${evaluation.type === 'PROACTIVE' ? '#6c757d' : '#007bff'}`,
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #444'
                      }}
                    >
                      <Card.Header 
                        style={{ 
                          cursor: 'pointer', 
                          backgroundColor: '#1a1a1a',
                          borderBottom: '1px solid #444',
                          color: '#e0e0e0'
                        }}
                        onClick={() => setExpandedEval(isExpanded ? null : evaluation.id)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Badge bg={evaluation.type === 'PROACTIVE' ? 'secondary' : 'primary'}>
                              {evaluation.type}
                            </Badge>
                            <Badge bg={getDecisionColor(evaluation.decision)}>
                              {getDecisionIcon(evaluation.decision)} {evaluation.decision}
                            </Badge>
                            <small style={{ color: '#888' }}>{formatTime(evaluation.timestamp)}</small>
                            {evaluation.confidence && (
                              <Badge bg="dark" style={{ border: '1px solid #666' }}>
                                {evaluation.confidence}% confidence
                              </Badge>
                            )}
                          </div>
                          <span style={{ fontSize: '14px', color: '#6c757d' }}>
                            {isExpanded ? '▲ Collapse' : '▼ Expand Details'}
                          </span>
                        </div>
                      </Card.Header>
                      
                      <Collapse in={isExpanded}>
                        <Card.Body style={{ backgroundColor: '#1a1a1a', color: '#e0e0e0' }}>
                          
                          {/* Error Banner */}
                          {evaluation.error && (
                            <div 
                              className="alert mb-3" 
                              style={{ 
                                backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                border: '1px solid #ffc107',
                                color: '#ffc107'
                              }}
                            >
                              <strong>⚠️ Error:</strong> {evaluation.error}
                            </div>
                          )}

                          {/* Main Reasoning */}
                          <div className="mb-3">
                            <h6 className="mb-2" style={{ color: '#888' }}>💭 Reasoning</h6>
                            <div 
                              className="p-3 rounded" 
                              style={{ 
                                backgroundColor: '#2a2a2a',
                                border: '1px solid #444',
                                whiteSpace: 'pre-wrap',
                                color: '#e0e0e0'
                              }}
                            >
                              {evaluation.reasoning}
                            </div>
                          </div>

                          {/* Execution Time */}
                          {evaluation.execution_time && (
                            <div className="mb-3">
                              <small style={{ color: '#888' }}>
                                ⏱️ Execution Time: {formatNumber(evaluation.execution_time, 1)}s
                              </small>
                            </div>
                          )}

                          {/* All remaining agent story sections follow the same pattern... */}
                          {/* I'll include the key sections to show the pattern */}

                          {/* Exit Risk */}
                          {agentStory.exit_risk && (
                            <div 
                              className="mb-3 p-3 rounded" 
                              style={{ 
                                backgroundColor: '#2a2a2a',
                                border: `2px solid ${
                                  agentStory.exit_risk.risk_level === 'LOW' ? '#10b981' : 
                                  agentStory.exit_risk.risk_level === 'MED' ? '#fbbf24' : '#ef4444'
                                }`
                              }}
                            >
                              <h6 className="mb-2" style={{ color: '#888' }}>⚠️ Exit Risk Analysis</h6>
                              <Badge bg={
                                agentStory.exit_risk.risk_level === 'LOW' ? 'success' : 
                                agentStory.exit_risk.risk_level === 'MED' ? 'warning' : 'danger'
                              } className="mb-2">
                                Risk Level: {agentStory.exit_risk.risk_level}
                              </Badge>
                              {agentStory.exit_risk.profit_left && (
                                <div><small style={{ color: '#aaa' }}>Profit Left: {agentStory.exit_risk.profit_left}</small></div>
                              )}
                              {agentStory.exit_risk.reason && (
                                <div><small style={{ color: '#aaa' }}>{agentStory.exit_risk.reason}</small></div>
                              )}
                            </div>
                          )}

                          {/* Red Flags */}
                          {agentStory.red_flags && agentStory.red_flags.length > 0 && (
                            <div 
                              className="mb-3 p-3 rounded" 
                              style={{ 
                                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                border: '1px solid #ef4444'
                              }}
                            >
                              <h6 className="mb-2" style={{ color: '#ef4444' }}>🚩 Red Flags</h6>
                              <ul className="mb-0">
                                {agentStory.red_flags.map((flag, i) => (
                                  <li key={i}><small style={{ color: '#aaa' }}>{flag}</small></li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Decision Journey - only showing partial for space */}
                          {agentStory.journey && agentStory.journey.steps && agentStory.journey.steps.length > 0 && (
                            <div className="mb-3">
                              <h6 style={{ color: '#888' }}>🗺️ Decision Journey ({agentStory.journey.path})</h6>
                              {agentStory.journey.steps.map((step, i) => (
                                <div 
                                  key={i} 
                                  className="mb-2 p-3 rounded border-start"
                                  style={{ 
                                    backgroundColor: '#2a2a2a',
                                    borderLeft: '3px solid #3b82f6 !important',
                                    border: '1px solid #444'
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <Badge bg="primary">{step.step || step.name || `Step ${i + 1}`}</Badge>
                                    {step.decision && <Badge bg="secondary">{step.decision}</Badge>}
                                    {step.result && <Badge bg="info">{step.result}</Badge>}
                                  </div>
                                  {step.reason && (
                                    <small style={{ color: '#aaa' }}>{step.reason}</small>
                                  )}
                                  {step.detail && (
                                    <small className="d-block mt-1" style={{ color: '#aaa' }}>{step.detail}</small>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Note: All other agent story sections (Thinking, Memory, Signals, Trading Logic, etc.) 
                               follow the same dark theme pattern as shown in EvaluationDetailModal */}

                        </Card.Body>
                      </Collapse>
                    </Card>
                  );
                })}

                {/* Show More/Less Button */}
                {positionEvaluations.length > 5 && (
                  <div className="text-center mt-3">
                    <Button 
                      size="sm" 
                      variant="outline-secondary"
                      onClick={() => setShowAllEvaluations(!showAllEvaluations)}
                      style={{ color: '#e0e0e0', borderColor: '#555' }}
                    >
                      {showAllEvaluations 
                        ? `▲ Show Less` 
                        : `▼ Show ${positionEvaluations.length - 5} More Evaluations`
                      }
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

      </Modal.Body>

      <Modal.Footer style={{ 
        backgroundColor: '#2a2a2a',
        borderTop: '1px solid #444'
      }}>
        {!isClosed && (
          <Button 
            variant="danger"
            style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
          >
            Close Position
          </Button>
        )}
        <Button 
          variant="outline-secondary"
          onClick={onHide}
          style={{ color: '#e0e0e0', borderColor: '#555' }}
        >
          Close
        </Button>
      </Modal.Footer>
      </div>
    </Modal>
  );
};

export default PositionDetailModal;